// src/components/PatientCard.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Fixed calculateAge function that handles empty/null DOB
export const calculateAge = (dob) => {
  if (!dob) return 'N/A';

  const birthDate = new Date(dob);

  // Check if date is valid
  if (isNaN(birthDate.getTime())) {
    return 'N/A';
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

const PatientCard = ({ patient }) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const [latestFeeling, setLatestFeeling] = useState(null); // Latest feeling
  const [feelingLoading, setFeelingLoading] = useState(true);
  const [feelingError, setFeelingError] = useState(false);

  const age = calculateAge(patient.dob);

  // Fetch Latest's feeling for the patient
  const fetchLatestFeeling = useCallback(async () => {
    try {
      setFeelingLoading(true);
      setFeelingError(false);
      
      // console.log(`🔍 FETCHING feeling for MRN: ${patient.MRN}`);
      
      const response = await axios.get(
        API_BASE_URL + `/patient-daily-feelings/patient/${patient.MRN}/latest`
      );
      
      // console.log(`✅ SUCCESS for MRN ${patient.MRN}:`, response.data);
      setLatestFeeling(response.data);
      
    } catch (error) {
      // console.log(`❌ FAILED for MRN ${patient.MRN}:`, error.message);
      
      if (error.response?.status === 404) {
        // No feeling recorded today - this is normal, not an error
        setLatestFeeling(null);  
      } else {
        // Actual error occurred
        setFeelingError(true);
      }
    } finally {
      setFeelingLoading(false);
    }
  }, [patient.MRN, API_BASE_URL]);

  useEffect(() => {
    if (patient.MRN) {
      fetchLatestFeeling();
    }
  }, [patient.MRN, fetchLatestFeeling]);

  const getFeelingColor = (feeling) => {
    switch (feeling?.toLowerCase()) {
      case 'stable':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'unwell':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Latest feeling tooltip
  const getLatestFeelingTooltip = (dateString) => {
    if (!dateString) return "Latest feeling";
  
    try {
      const recordedDate = new Date(dateString);
      const now = new Date();
  
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
  
      const recordDay = new Date(
        recordedDate.getFullYear(),
        recordedDate.getMonth(),
        recordedDate.getDate()
      );
  
      const time = recordedDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
  
      if (recordDay.getTime() === today.getTime()) {
        return `Latest feeling taken Today at ${time}`;
      }
  
      if (recordDay.getTime() === yesterday.getTime()) {
        return `Latest feeling taken Yesterday at ${time}`;
      }
  
      const date = recordedDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: recordedDate.getFullYear() !== now.getFullYear()
          ? "numeric"
          : undefined,
      });
  
      return `Latest feeling taken ${date} at ${time}`;
    } catch {
      return "Latest feeling";
    }
  };
  
  // Render feeling indicator
  const renderFeelingIndicator = () => {
    if (feelingLoading) {
      return (
        <div className="flex items-center space-x-1" title="Latest feeling">
          <div className="w-3 h-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-xs text-gray-500">Loading...</span>
        </div>
      );
    }

    if (feelingError) {
      return (
        <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded border border-red-200" 
              title="Latest feeling">
          Error
        </span>
      );
    }

    if (latestFeeling) {
  return (
    <div
      className="flex flex-col items-end"
      title={getLatestFeelingTooltip(latestFeeling.recordedAt)}
    >
      <span
        className={`px-2 py-1 rounded text-xs font-semibold border ${getFeelingColor(
          latestFeeling.feelingTag
        )}`}
      >
        {latestFeeling.feelingTag}
      </span>
          {/* {latestFeeling.recordedAt && (
            <span className="text-xs text-gray-400 mt-1">
                {formatTime(latestFeeling.recordedAt)}
            </span>
          )} */}
        </div>
      );
    }

    return (
      <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded border border-gray-200" 
            title="Latest feeling">
        Not recorded
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 p-4 border-l-4 border-teal-500">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
            <span className="text-teal-700 font-bold text-lg">
              {patient.firstName?.charAt(0)}{patient.lastName?.charAt(0)}
            </span>
          </div>
          <div>
            <h3 className="font-semibold text-lg text-gray-800">
              {patient.firstName} {patient.lastName}
            </h3>
            <p className="text-xs text-gray-500">MRN: {patient.MRN}</p>
          </div>
        </div>
        
        <div className="flex items-start space-x-2">
          {/* Feeling Indicator */}
          {renderFeelingIndicator()}
          
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Age:</span>
          <span className="font-medium text-gray-800">{age}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Gender:</span>
          <span className="font-medium text-gray-800">{patient.gender || 'N/A'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Diagnosis:</span>
          <span className="font-medium text-gray-800">{patient.diagnosis || 'N/A'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Last Check-up:</span>
          <span className="font-medium text-gray-800">{patient.lastCheckUp || 'N/A'}</span>
        </div>
      </div>
    </div>
  );
};

export default PatientCard;





// // src/components/PatientCard.js
// import React from 'react';

// // Fixed calculateAge function that handles empty/null DOB
// export const calculateAge = (dob) => {
//   if (!dob) return 'N/A';

//   const birthDate = new Date(dob);

//   // Check if date is valid
//   if (isNaN(birthDate.getTime())) {
//     return 'N/A';
//   }

//   const today = new Date();
//   let age = today.getFullYear() - birthDate.getFullYear();
//   const monthDiff = today.getMonth() - birthDate.getMonth();

//   if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
//     age--;
//   }

//   return age;
// };

// const PatientCard = ({ patient }) => {
//   // console.log("Patient data received:", patient);
//   const age = calculateAge(patient.dob);

//   const getStatusColor = (status) => {
//     switch (status) {
//       case 'Critical':
//         return 'bg-red-100 text-red-800';
//       case 'High Risk':
//         return 'bg-yellow-100 text-yellow-800';
//       case 'Stable':
//         return 'bg-green-100 text-green-800';
//       default:
//         return 'bg-gray-100 text-gray-800';
//     }
//   };

//   return (
//     <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-4 border-l-4 border-teal-500">
//       <div className="flex items-start justify-between mb-3">
//         <div className="flex items-center space-x-3">
//           <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
//             <span className="text-teal-700 font-bold text-lg">
//               {patient.firstName?.charAt(0)}{patient.lastName?.charAt(0)}
//             </span>
//           </div>
//           <div>
//             <h3 className="font-semibold text-lg text-gray-800">
//               {patient.firstName} {patient.lastName}
//             </h3>
//             <p className="text-xs text-gray-500">MRN: {patient.MRN}</p>
//           </div>
//         </div>
//         <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(patient.status)}`}>
//           {patient.status}
//         </span>
//       </div>

//       <div className="space-y-2 text-sm">
//         <div className="flex justify-between">
//           <span className="text-gray-600">Age:</span>
//           <span className="font-medium text-gray-800">{age}</span>
//         </div>
//         <div className="flex justify-between">
//           <span className="text-gray-600">Gender:</span>
//           <span className="font-medium text-gray-800">{patient.gender || 'N/A'}</span>
//         </div>
//         <div className="flex justify-between">
//           <span className="text-gray-600">Diagnosis:</span>
//           <span className="font-medium text-gray-800">{patient.diagnosis || 'N/A'}</span>
//         </div>

//         {/* <div className="flex justify-between">
           
//             <span className="text-gray-600">Clinician:</span>
//             <span className="font-medium text-gray-800">
//               {patient.patient_doctors && patient.patient_doctors.length > 0
//                 ? patient.patient_doctors
//                     .map(pd => pd.clinician 
//                         ? `${pd.clinician.firstName} ${pd.clinician.lastName}` 
//                         : "N/A")
//                     .join(", ")
//                 : "N/A"}
//             </span>


//         </div> */}

//         <div className="flex justify-between">
//           <span className="text-gray-600">Last Check-up:</span>
//           <span className="font-medium text-gray-800">{patient.lastCheckUp || 'N/A'}</span>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default PatientCard;

// // // components/PatientCard.js

// // import React from 'react';

// // const PatientCard = ({ patient }) => {
// //   const age = patient.dob ? calculateAge(patient.dob) : 'N/A';
// //   return (
// //     <div className="border rounded-md shadow-sm p-4 bg-white mb-4">
// //       <div className="flex justify-between items-start">
// //         <div>
// //           <h3 className="text-lg font-semibold">{patient.firstName} {patient.lastName}</h3>
// //           <p className="text-sm text-gray-500">{patient.gender}, Age: {age}</p>
// //           <p className="text-sm text-gray-500">{patient.diagnosis}</p>
// //         </div>
// //         <span
// //           className={`
// //             text-xs px-2 py-1 rounded
// //             ${patient.status === 'Critical' ? 'bg-red-100 text-red-800' :
// //               patient.status === 'High Risk' ? 'bg-yellow-100 text-yellow-800' :
// //                 patient.status === 'Stable' ? 'bg-green-100 text-green-800' :
// //                   'bg-blue-100 text-blue-800'}`}  // Default color if status is not matched
// //         >
// //           {patient.status}
// //         </span>
// //       </div>
// //       <div className="flex justify-between mt-4 text-sm text-gray-700">
// //         <div>BP: {patient.bp}</div>
// //         <div>HR: {patient.heartRate}</div>
// //         <div>Last checkup: {patient.lastCheckup}</div>
// //       </div>
// //     </div>
// //   );
// // };

// // // Helper function
// // export const calculateAge = (dob) => {
// //   const birthDate = new Date(dob);
// //   if (isNaN(birthDate)) {
// //     console.warn("Invalid dob:", dob);
// //     return "N/A";
// //   }
// //   const today = new Date();
// //   let age = today.getFullYear() - birthDate.getFullYear();
// //   const m = today.getMonth() - birthDate.getMonth();

// //   if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
// //     age--;
// //   }

// //   return age;
// // };

// // export default PatientCard;
